import * as React from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useSuspenseQuery, useQuery } from '@tanstack/react-query';
import { productQueryOptions, smartSwapsQueryOptions } from '@/web/lib/queryOptions';
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen';
import { Card, CardContent } from '@/web/components/ui/card';
import { Separator } from '@/web/components/ui/separator';
import { ProductCard } from '@/web/components/molecules/ProductCard';
import { SmartSwapProductCard } from '@/web/components/molecules/SmartSwapProductCard';
import { Button } from '@/web/components/ui/button';
import { ChevronLeftIcon } from 'lucide-react';

export const Route = createFileRoute('/products/$categoryId/details/$productId')({
  loader: async ({ params, context }) => {
    return context.queryClient?.ensureQueryData(productQueryOptions(params.productId));
  },
  component: ProductDetailsComponent,
  pendingComponent: LoadingScreen,
  errorComponent: () => (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-center">
      <h2 className="text-2xl font-bold mb-2">Oops!</h2>
      <p className="text-muted-foreground mb-4">We couldn't find the product you're looking for.</p>
      <button 
        onClick={() => window.history.back()}
        className="text-primary hover:underline"
      >
        Go back
      </button>
    </div>
  ),
});

function ProductDetailsComponent() {
  const { productId } = useParams({ from: '/products/$categoryId/details/$productId' });
  const { data: product } = useSuspenseQuery(productQueryOptions(productId));
  const { data: smartSwaps, isLoading: isSwapsLoading } = useQuery(smartSwapsQueryOptions(productId));
  const [activeTab, setActiveTab] = React.useState<'details' | 'swaps'>('details');
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  if (!product) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % product.imageUrls.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + product.imageUrls.length) % product.imageUrls.length);
  };

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4 -ml-2 text-muted-foreground cursor-pointer hover:text-foreground"
        onClick={() => window.history.back()}
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back
      </Button>
      {/* Brand and Product Name */}
      <div className="mb-6">
        {product.brand && (
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {product.brand}
          </h2>
        )}
        <h1 className="text-3xl font-bold">{product.name}</h1>
      </div>

      {/* Image Carousel */}
      <div className="relative mb-8 group">
        <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
          {product.imageUrls && product.imageUrls.length > 0 ? (
            <img
              src={product.imageUrls[currentImageIndex]}
              alt={product.name}
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="text-muted-foreground">No image available</div>
          )}
        </div>
        {product.imageUrls && product.imageUrls.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-colors"
            >
              ←
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 p-2 rounded-full shadow-md hover:bg-white transition-colors"
            >
              →
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1">
              {product.imageUrls.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full ${
                    i === currentImageIndex ? 'bg-primary' : 'bg-primary/20'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Price and Unit of Measure */}
      <div className="mb-8">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold">
            ${(product.price.amountAsCents / 100).toFixed(2)}
          </span>
          {product.price.wasAmountAsCents && product.price.wasAmountAsCents > product.price.amountAsCents && (
            <span className="text-lg text-muted-foreground line-through">
              ${(product.price.wasAmountAsCents / 100).toFixed(2)}
            </span>
          )}
        </div>
        <div className="text-muted-foreground mt-1">
          {product.price.comparativePricing.quantity} {product.price.comparativePricing.unitOfMeasure}
        </div>
      </div>

      <Separator className="my-6" />

      {/* Segmented Control / Tabs */}
      <div className="flex p-1 bg-muted rounded-lg mb-6">
        <button
          onClick={() => setActiveTab('details')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            activeTab === 'details'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Product Details
        </button>
        <button
          onClick={() => setActiveTab('swaps')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            activeTab === 'swaps'
              ? 'bg-background shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Smart Swaps
        </button>
      </div>

      {/* Tab Content */}
      <Card>
        <CardContent className="pt-6">
          {activeTab === 'details' ? (
            <div className="space-y-6">
              <section>
                <h3 className="font-semibold mb-2">Ingredients</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {product.ingredients || 'No ingredients information available.'}
                </p>
              </section>
              <section>
                <h3 className="font-semibold mb-2">Allergens</h3>
                <p className="text-sm text-muted-foreground">
                  {product.allergens || 'None reported.'}
                </p>
              </section>
              <section>
                <h3 className="font-semibold mb-2">Country of Origin</h3>
                <p className="text-sm text-muted-foreground">
                  {product.countryOfOrigin || 'Not specified.'}
                </p>
              </section>
            </div>
          ) : (
            <div className="space-y-4">
              {isSwapsLoading ? (
                <div className="py-12 text-center text-muted-foreground">Loading smart swaps...</div>
              ) : smartSwaps && smartSwaps.length > 0 ? (
                <div className="grid grid-cols-1 gap-4">
                  {smartSwaps.map((swap) => (
                    <SmartSwapProductCard key={swap.product.productId} smartSwap={swap} />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                    ✨
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No Smart Swaps</h3>
                  <p className="text-muted-foreground max-w-xs mx-auto">
                    We couldn't find any smart swaps for this product yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
